import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import SportGroupsListTypes from 'modules/sport-groups-list/types';
import SportGroupsListSkeleton from 'modules/sport-groups-list/skeleton/index.js';
import SingleLeagueCouponTypes from 'modules/single-league-coupon/types.js';
import CountryOrSportIcon from 'modules/country-or-sport-icon/index.js';

const classUtils = MojitoCore.Base.classUtils;
const { selectItemsState } = MojitoServices.SportsContent.ContainerItems.selectors;
const {
    Text,
    FlexPane,
    Image,
    Button,
    LoaderSuspense,
    GridLayout: { GridPane },
    ImageButton,
} = MojitoPresentation.Components;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { SortingHelper } = MojitoPresentation.Utils;
const { EventFormatter } = MojitoPresentation.Utils;
const { useAppContext } = MojitoCore.Presentation.Hooks;
const SportUtils = MojitoServices.SportsContent.Sports.utils;

const {
    CONTENT_STATE: { UNKNOWN, PENDING },
} = MojitoServices.Common.types;
const { COUPONS } = SingleLeagueCouponTypes;
const { SORTING_TYPES } = SportGroupsListTypes;

const sortByName = (items, language) =>
    [...items].sort((a, b) => SortingHelper.compare(a.name, b.name, language));
const sortByDisplayOrder = items => [...items].sort((a, b) => a.displayOrder - b.displayOrder);

const useItemsSorting = (items, sortingType) => {
    const { systemSettings } = useAppContext();
    const language = systemSettings().language;
    const sortingTypeMap = {
        [SORTING_TYPES.BY_NAME]: sortByName,
        [SORTING_TYPES.BY_DISPLAY_ORDER]: sortByDisplayOrder,
    };
    const sortingFunc = sortingTypeMap[sortingType];

    return useMemo(
        () => (sortingFunc ? sortingFunc(items || [], language) : items),
        [sortingFunc, items, language]
    );
};

const SportGroupsListView = props => {
    const {
        groupId,
        sportId,
        sportGroups,
        type,
        pageType,
        onDataLoad,
        mojitoTools,
        class: className,
    } = props;
    const {
        config,
        appContext: {
            routeResolver,
            analyticsEmitter: { emitAnalytics },
        },
        stringResolver,
    } = mojitoTools;
    const isTopSportGroups = type === SportGroupsListTypes.TYPE.TOP;
    const competitionsName =
        stringResolver.resolveString(`$SPORT_GROUPS_LIST.COMPETITIONS_NAME.${sportId}`, true) ||
        stringResolver.resolveString(`$SPORT_GROUPS_LIST.COMPETITIONS_NAME.DEFAULT`);

    const headerText = stringResolver.resolveString(`$SPORT_GROUPS_LIST.${type}.${sportId}`, true)
        ? stringResolver.resolveAndFormatString(
              `$SPORT_GROUPS_LIST.${type}.${sportId}`,
              competitionsName
          )
        : stringResolver.resolveAndFormatString(
              `$SPORT_GROUPS_LIST.${type}.DEFAULT`,
              competitionsName
          );

    const createHref = (typeId, linkedSportId, eventId) => {
        if (SportUtils.isRacingSport(sportId)) {
            return routeResolver.getRoute(NavigationPathBuilder.toFutureRaceEvent(sportId, typeId));
        }
        if (pageType === COUPONS.COMPETITIONS) {
            return routeResolver.getRoute(
                NavigationPathBuilder.toSportCompetitionsLeague(linkedSportId || sportId, typeId)
            );
        }
        if (pageType === COUPONS.OUTRIGHTS) {
            const desiredSportId = linkedSportId || sportId;
            const navigationPayload = eventId
                ? NavigationPathBuilder.toSportEvent(desiredSportId, eventId)
                : NavigationPathBuilder.toSportOutrightsLeague(desiredSportId, typeId);
            return routeResolver.getRoute(navigationPayload);
        }
    };

    const onClick = (typeId, eventId, linkedSportId) => {
        const resolvedSportId = linkedSportId || sportId;
        if (isTopSportGroups) {
            emitAnalytics('topLeagueClick', typeId);
        }
        if (SportUtils.isRacingSport(sportId)) {
            return IntentActionTrigger.showFutureRaceEventPage(sportId, typeId);
        }
        if (pageType === COUPONS.COMPETITIONS) {
            IntentActionTrigger.showSportCompetitions(resolvedSportId, typeId);
        } else if (pageType === COUPONS.OUTRIGHTS) {
            if (!eventId) {
                IntentActionTrigger.showSportOutrights(resolvedSportId, typeId);
            } else {
                IntentActionTrigger.showSportEvent(resolvedSportId, eventId);
            }
        }
    };

    const isContentPending = useSelector(state => {
        const loadingState = selectItemsState(groupId, state);
        return loadingState === UNKNOWN || loadingState === PENDING;
    });

    const skeleton = (
        <SportGroupsListSkeleton
            headerText={headerText}
            isTopSportGroups={isTopSportGroups}
            config={config.sportGroupsListSkeleton}
        />
    );

    useEffect(() => {
        if (!isContentPending && onDataLoad && sportGroups?.length) {
            onDataLoad(sportGroups);
        }
    }, [isContentPending, onDataLoad, sportGroups]);

    const [sortingType, setSortingType] = useState(config.defaultSortingMethod);
    const isSortingEnabled =
        sportGroups.length >= config.minimumClassesToEnableSorting && config.enableSorting;

    return (
        <LoaderSuspense
            config={config.skeletonLoader}
            isContentPending={isContentPending}
            loader={skeleton}
        >
            {sportGroups.length > 0 && (
                <FlexPane
                    config={config.container}
                    class={classUtils.classNames('ta-SportGroupsListView', className)}
                >
                    {!SportUtils.isRacingSport(sportId) && (
                        <FlexPane config={config.headerContainer}>
                            <Text config={config.headerText}>{headerText}</Text>
                            {isSortingEnabled && (
                                <SortingButton
                                    config={config}
                                    stringResolver={stringResolver}
                                    emitAnalytics={emitAnalytics}
                                    sportGroups={sportGroups}
                                    sportId={sportId}
                                    sortingType={sortingType}
                                    setSortingType={setSortingType}
                                />
                            )}
                        </FlexPane>
                    )}
                    <FlexPane config={config.mainContainer} class="ta-SportGroupsListViewContent">
                        {isTopSportGroups ? (
                            <TopSportGroups
                                sportId={sportId}
                                sportGroups={sportGroups}
                                createHref={createHref}
                                onClick={onClick}
                                mojitoTools={mojitoTools}
                            />
                        ) : (
                            <AllSportGroups
                                sportId={sportId}
                                sportGroups={sportGroups}
                                createHref={createHref}
                                onClick={onClick}
                                mojitoTools={mojitoTools}
                                sortingType={sortingType}
                            />
                        )}
                    </FlexPane>
                </FlexPane>
            )}
        </LoaderSuspense>
    );
};

const SortingButton = props => {
    const { config, stringResolver, emitAnalytics, sportId, sortingType, setSortingType } = props;

    const onToggleSortingButton = () => {
        setSortingType(prevType =>
            prevType === SORTING_TYPES.BY_NAME
                ? SORTING_TYPES.BY_DISPLAY_ORDER
                : SORTING_TYPES.BY_NAME
        );
        emitAnalytics(`sortingClicked`, { sortBy: sortingType, sportId });
    };
    const sortingTitle =
        sortingType === SORTING_TYPES.BY_NAME
            ? stringResolver.resolveString(`$SPORT_GROUPS_LIST.SORTING_TYPE.BY_NAME`)
            : stringResolver.resolveString(`$SPORT_GROUPS_LIST.SORTING_TYPE.BY_DISPLAY_ORDER`);

    return (
        <FlexPane config={config.sortingButtonContainer}>
            <ImageButton
                onClick={onToggleSortingButton}
                label={sortingTitle}
                config={config.sortEventsToggleButton}
            />
        </FlexPane>
    );
};

const TopSportGroups = ({ sportId, sportGroups, createHref, onClick, mojitoTools }) => {
    const { config } = mojitoTools;

    return (
        <FlexPane config={config.listContainer}>
            <GridPane
                repeatedColumnsNum={config.sportGroupsGridColumns}
                config={config.sportGroupsGrid}
            >
                {sportGroups.map(
                    ({ name, id, countryCode, countryName, className, eventId, linkedSportId }) => (
                        <SportGroup
                            key={id}
                            sportId={sportId}
                            countryInfo={{ countryCode, countryName }}
                            name={name}
                            classname={className}
                            onClick={() => onClick(id, eventId, linkedSportId)}
                            hrefLink={createHref(id, sportId, eventId)}
                            mojitoTools={mojitoTools}
                        />
                    )
                )}
            </GridPane>
        </FlexPane>
    );
};

function canRenderCountryFlag(countryCode, config) {
    if (!countryCode) {
        return false;
    }

    const countryIcons = config?.countryOrSportIcon?.countryIcon?.countryIcons;

    return Boolean(countryIcons && countryIcons[countryCode]);
}

function shouldShowTypeCountryFlags(group, config) {
    const { items = [], countryCode } = group;

    if (items.length === 0) {
        return false;
    }

    // Suppress type flags ONLY if class renders a REAL country flag.
    if (canRenderCountryFlag(countryCode, config)) {
        return false;
    }

    // Otherwise allow type flags if at least one type has a valid country flag.
    return items.some(item => canRenderCountryFlag(item?.countryCode, config));
}

const AllSportGroups = props => {
    const { sportId, sportGroups, createHref, onClick, mojitoTools, sortingType } = props;
    const { config } = mojitoTools;
    const sortedSportGroups = useItemsSorting(sportGroups, sortingType);

    return sortedSportGroups.map(group => {
        const { name, id, items, countryCode, countryName, className, linkedSportId } = group;
        const resolvedSportId = linkedSportId || sportId;
        const shouldShowFlagOnType = shouldShowTypeCountryFlags(group, config);

        return (
            <FlexPane id={id} key={id} config={config.listContainer}>
                <FlexPane config={config.locationContainer}>
                    <CountryOrSportIcon
                        sportId={resolvedSportId}
                        countryCode={countryCode}
                        countryName={countryName}
                        config={config.countryOrSportIcon}
                    />
                    <Text config={config.locationName}>{name}</Text>
                </FlexPane>
                <SportLeagues
                    sportId={resolvedSportId}
                    classname={className}
                    createHref={createHref}
                    onClick={onClick}
                    mojitoTools={mojitoTools}
                    items={items}
                    sortingType={sortingType}
                    shouldShowFlagOnType={shouldShowFlagOnType}
                />
            </FlexPane>
        );
    });
};

const SportLeagues = props => {
    const {
        mojitoTools,
        sportId,
        className,
        onClick,
        createHref,
        items,
        sortingType,
        shouldShowFlagOnType,
    } = props;
    const { config } = mojitoTools;
    const sortedItems = useItemsSorting(items, sortingType) || [];

    return (
        <GridPane
            repeatedColumnsNum={config.sportGroupsGridColumns}
            config={config.sportGroupsGrid}
        >
            {sortedItems.map(league => {
                const countryInfo = {
                    countryCode: league.countryCode,
                    countryName: league.countryName,
                };

                const countryInfoForType = shouldShowFlagOnType
                    ? {
                          countryCode: league.countryCode,
                          countryName: league.countryName,
                      }
                    : undefined;

                return (
                    <SportGroup
                        key={league.id}
                        sportId={sportId}
                        name={league.name}
                        classname={className}
                        countryInfo={countryInfoForType ? countryInfo : undefined}
                        onClick={() => onClick(league.id, league.eventId, sportId)}
                        hrefLink={createHref(league.id, sportId, league.eventId)}
                        mojitoTools={mojitoTools}
                    />
                );
            })}
        </GridPane>
    );
};

const SportGroup = ({ sportId, countryInfo, name, classname, onClick, hrefLink, mojitoTools }) => {
    const { config } = mojitoTools;
    const labelText = classname ? `${classname} - ${name}` : EventFormatter.formatEventName(name);

    return (
        <Button
            config={config.sportGroupButton}
            class="ta-SportGroupsListButton"
            hrefLink={hrefLink}
            onClick={onClick}
        >
            {countryInfo && (
                <CountryOrSportIcon
                    sportId={sportId}
                    countryCode={countryInfo.countryCode}
                    countryName={countryInfo.countryName}
                    config={config.countryOrSportIcon}
                />
            )}
            <Text class={'ta-sportClassTypeName'} config={config.sportGroupName}>
                {labelText}
            </Text>
            <Image config={config.chevronIcon} />
        </Button>
    );
};

export default SportGroupsListView;
